/* eslint-disable @typescript-eslint/no-unused-expressions */
import {
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKRadio,
  Grid2,
  RadioGroup,
  Switch,
  Typography,
} from "components";
import GKCheckbox from "components/GKCheckbox";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { EquityBasketListInterface } from "interfaces/EquityBasket";
import DashboardLayout from "layouts/DashboardLayout";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { handleCreateFormData } from "utils/formHelpers";
import { refetchQuery } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";

const EquityBasketSettings = () => {
  useDocumentTitle("Basket Settings");

  const { equityBasketId: id } = useParams();

  /** ========================================================================================================= */
  const form = useForm({
    defaultValues: {
      allocationReferenceWeightage: false,
      rebalanceReferenceWeightage: false,
      rebalanceReferenceValue: "",
      publishPlatform: false,
      allowAllocation: false,
      selectedVersion: null,
      allowRebalance: false,
      published: false,
    },
  });

  /** ========================================================================================================= */
  const { isLoading: basketDetailsLoading } = useFetchQuery({
    key: ["GET_MODEL_PORTFOLIO_DETAILS_DATA"],
    route: `/model_portfolio/${id}/details/`,
    success: (response: { data: EquityBasketListInterface }) => {
      form.reset({
        allocationReferenceWeightage:
          response?.data?.allocationReferenceWeightage,
        rebalanceReferenceWeightage:
          response?.data?.rebalanceReferenceWeightage,
        rebalanceReferenceValue: response?.data?.rebalanceReferenceValue,
        allowAllocation: response?.data?.allowAllocation,
        selectedVersion: response?.data?.selectedVersion,
        allowRebalance: response?.data?.allowRebalance,
        published: response?.data?.published,
      });
    },
  });

  /** ========================================================================================================= */
  const { mutate: handleUpdateEquityBasket, isLoading: updateLoading } =
    useMutation({
      mutationFn: (data: any) =>
        axiosInstance.patch(`/model_portfolio/${id}/`, data),
      onSuccess: () => {
        refetchQuery("GET_MODEL_PORTFOLIO_DETAILS_DATA");
      },
    });

  return (
    <DashboardLayout title="Basket Edit" loading={basketDetailsLoading}>
      <Grid2 container spacing={1}>
        <Grid2
          size={12}
          overflow={"hidden auto"}
          minHeight={`calc(100vh - 129px)`}
          maxHeight={`calc(100vh - 129px)`}
          pr={1}
        >
          <form
            id="equity-basket-meta-form"
            onSubmit={form.handleSubmit((values: any) => {
              handleUpdateEquityBasket(
                handleCreateFormData({
                  values: values,
                  dirtyFields: form.formState.dirtyFields,
                }),
              );
            })}
          >
            <Grid2 container spacing={1}>
              <Grid2 size={3} display={"flex"}>
                {updateLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Controller
                    name="published"
                    control={form.control}
                    render={({ field }) => {
                      return (
                        <Switch
                          checked={field.value}
                          onChange={(e) => {
                            handleUpdateEquityBasket(
                              handleCreateFormData({
                                values: { published: e.target.checked },
                                dirtyFields: {
                                  published: true,
                                },
                              }),
                            );
                          }}
                        />
                      );
                    }}
                  />
                )}
                <Typography fontSize={18} fontWeight={600} lineHeight={1}>
                  Publish Basket
                </Typography>
              </Grid2>

              <Grid2 size={9}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12} display={"flex"} flexDirection={"column"}>
                    <Typography color={"var(--neutral-500)"}>
                      Publish basket on
                    </Typography>
                    <Controller
                      name="publishPlatform"
                      control={form.control}
                      render={({ field }) => {
                        return (
                          <FormControlLabel
                            {...field}
                            label="Micro-Website"
                            control={
                              <GKCheckbox
                                disableLeftPadding
                                checked
                                onChange={(event) => {
                                  field.onChange(event.target.checked);
                                }}
                              />
                            }
                          />
                        );
                      }}
                    />
                    <Controller
                      name="publishPlatform"
                      control={form.control}
                      render={({ field }) => {
                        return (
                          <FormControlLabel
                            {...field}
                            label="Reporting Manager-Admins"
                            disabled
                            control={
                              <GKCheckbox
                                disableLeftPadding
                                checked={field.value}
                                onChange={(event) => {
                                  field.onChange(event.target.checked);
                                }}
                              />
                            }
                          />
                        );
                      }}
                    />
                    <Controller
                      name="publishPlatform"
                      control={form.control}
                      render={({ field }) => {
                        return (
                          <FormControlLabel
                            {...field}
                            label="Channel Partners"
                            disabled
                            control={
                              <GKCheckbox
                                disableLeftPadding
                                checked={field.value}
                                onChange={(event) => {
                                  field.onChange(event.target.checked);
                                }}
                              />
                            }
                          />
                        );
                      }}
                    />
                    <Controller
                      name="publishPlatform"
                      control={form.control}
                      render={({ field }) => {
                        return (
                          <FormControlLabel
                            {...field}
                            disabled
                            label="Gridkey partners"
                            control={
                              <GKCheckbox
                                disableLeftPadding
                                checked={field.value}
                                onChange={(event) => {
                                  field.onChange(event.target.checked);
                                }}
                              />
                            }
                          />
                        );
                      }}
                    />
                  </Grid2>
                  <Grid2 size={12} textAlign={"end"}>
                    <Divider />
                  </Grid2>
                  <Grid2 size={12}>
                    <Grid2 container>
                      <Grid2 size={12}>
                        <Controller
                          name="allowAllocation"
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <FormControlLabel
                                {...field}
                                sx={{
                                  "&.MuiFormControlLabel-root .MuiTypography-root":
                                    {
                                      fontSize: 20,
                                    },
                                }}
                                label="Allocation"
                                control={
                                  <GKCheckbox
                                    disableLeftPadding
                                    checked={field.value}
                                    onChange={(event) => {
                                      field.onChange(event.target.checked);
                                    }}
                                  />
                                }
                              />
                            );
                          }}
                        />
                      </Grid2>
                      <Grid2 size={6}>
                        <Controller
                          name="allocationReferenceWeightage"
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <FormControl>
                                <FormLabel>
                                  Allocation Reference Weightage
                                </FormLabel>
                                <RadioGroup {...field}>
                                  <FormControlLabel
                                    value="true"
                                    control={<GKRadio />}
                                    label="Allocated"
                                  />
                                  <FormControlLabel
                                    value="false"
                                    control={<GKRadio />}
                                    label="Current"
                                  />
                                </RadioGroup>
                              </FormControl>
                            );
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={12} textAlign={"end"}>
                    <Divider />
                  </Grid2>
                  <Grid2 size={12}>
                    <Grid2 container>
                      <Grid2 size={12}>
                        <Controller
                          name="allowRebalance"
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <FormControlLabel
                                {...field}
                                sx={{
                                  "&.MuiFormControlLabel-root .MuiTypography-root":
                                    {
                                      fontSize: 20,
                                    },
                                }}
                                label="Rebalance"
                                control={
                                  <GKCheckbox
                                    disableLeftPadding
                                    checked={field.value}
                                    onChange={(event) => {
                                      field.onChange(event.target.checked);
                                    }}
                                  />
                                }
                              />
                            );
                          }}
                        />
                      </Grid2>
                      <Grid2 size={4}>
                        <Controller
                          name="rebalanceReferenceWeightage"
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <FormControl>
                                <FormLabel id="demo-radio-buttons-group-label">
                                  Rebalance Reference Weightage
                                </FormLabel>
                                <RadioGroup {...field}>
                                  <RadioGroup {...field}>
                                    <FormControlLabel
                                      value="true"
                                      control={<GKRadio />}
                                      label="Allocated"
                                    />
                                    <FormControlLabel
                                      value="false"
                                      control={<GKRadio />}
                                      label="Current"
                                    />
                                  </RadioGroup>
                                </RadioGroup>
                              </FormControl>
                            );
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Grid2>
              </Grid2>
            </Grid2>
          </form>
        </Grid2>
        <Grid2 size={12} textAlign={"end"}>
          <Divider />
        </Grid2>
        <Grid2 size={12} textAlign={"end"}>
          <GKButton
            type="submit"
            form="equity-basket-meta-form"
            variant="contained"
            size="medium"
            loading={updateLoading}
          >
            {id ? "Save" : "Submit"}
          </GKButton>
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default EquityBasketSettings;
